import { useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';

/* ----------------------------------------------------------------------------
   Marketplace spotlight — the dark "money shot" section that answers the
   inventor's next fear after a verdict: "who do I actually take this to, and
   how do I not get fleeced?" Replaces the old memo-proof spotlight (the memos
   are already shown throughout the climb above, so that was doubling up).

   The headline asks the question; the specimen card is the answer — a sample
   directory result whose honest hook is a MATCH COUNT, with one anonymized
   profile carrying the real USPTO credential signals (name + reg number
   redacted, never a fabricated person/face).
   -------------------------------------------------------------------------- */

// The full USPTO roster (breadth / credibility) and the matched shortlist
// (relevance). The section reads as a funnel: 50,000+ total → your 12 matches.
// 50,000+ is honest against the live USPTO OED roster (~53,400 active
// practitioners); rounded down with a "+" so it never overclaims or goes stale.
const ROSTER_TARGET = 50000;
const MATCH_TARGET = 12;

/** LOOPING count-up: while the element is in view, count 0 -> target over
    `duration`, HOLD on the target for `hold` ms, then reset and count again —
    continuously (founder request). Pauses when scrolled off screen (perf) and
    restarts from 0 on return. Throttled to ~25fps so the loop is smooth without
    flooding renders. Reduced-motion shows the final number static (no loop).
    tabular-nums + centered layout = no horizontal layout shift while the digits
    (and comma) change. `duration` lets the big roster number take a touch longer
    than the small match count. */
function useCountUp(target: number, duration = 900, hold = 2600) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setValue(target);
      return;
    }
    let raf = 0;
    let start = 0;
    let last = 0;
    let running = false;
    const cycle = duration + hold; // count phase + dwell on the final number
    const loop = (now: number) => {
      if (!start) start = now;
      // Throttle to ~25fps — smooth count, gentle on renders (two of these run).
      if (now - last >= 40) {
        const phase = (now - start) % cycle;
        if (phase < duration) {
          const t = phase / duration;
          const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
          setValue(Math.round(eased * target));
        } else {
          setValue(target); // hold on target (React skips re-render if unchanged)
        }
        last = now;
      }
      raf = requestAnimationFrame(loop);
    };
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          if (!running) {
            running = true;
            start = 0; // restart the count from 0 each time it re-enters view
            last = 0;
            raf = requestAnimationFrame(loop);
          }
        } else if (running) {
          running = false;
          cancelAnimationFrame(raf);
        }
      },
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [target, duration, hold]);

  return { value, ref };
}

export function MarketplaceSpotlight() {
  const { value, ref } = useCountUp(MATCH_TARGET);
  // Big roster number — counts a touch slower than the match count for weight.
  const { value: rosterValue, ref: rosterRef } = useCountUp(ROSTER_TARGET, 1400);

  return (
    <section className="mspot">
      <style>{MSPOT_CSS}</style>
      <div className="mspot__inner">
        <div className="mspot__eyebrow">
          <span className="mspot__tick" aria-hidden="true" />
          The attorney marketplace
        </div>

        <h2 className="mspot__headline f-display">
          Now, who do you
          <br />
          <span className="mspot__head-em">actually</span> take it to?
        </h2>

        <p className="mspot__sub">
          Every USPTO-registered patent practitioner in the country, in one
          searchable directory. Shortlist faster, and walk into your first
          attorney call prepared.
        </p>

        {/* Prominent roster stat — the marketplace's headline credibility number,
            animated on scroll-in. It's the top of the funnel (the whole roster);
            the card below narrows it to the visitor's matched shortlist. */}
        <div className="mspot__roster" ref={rosterRef}>
          <div className="mspot__roster-num f-display">
            {rosterValue.toLocaleString()}
            <span className="mspot__roster-plus">+</span>
          </div>
          <div className="mspot__roster-label">
            USPTO-registered patent attorneys &amp; agents, all in one place
          </div>
        </div>

        {/* Specimen card — sample directory result */}
        <div className="mspot__card" ref={ref}>
          <div className="mspot__card-head">
            <span className="mspot__card-label">Matched practitioners</span>
            <span className="mspot__filter">CA · Mechanical · Micro-entity OK</span>
          </div>

          <div className="mspot__count f-display">{value}</div>
          <div className="mspot__count-label">
            Registered practitioners match your field and filters.
          </div>

          <div className="mspot__rule" />

          {/* Anonymized profile — every real signal, identity redacted */}
          <div className="mspot__profile">
            <div className="mspot__profile-top">
              <div className="mspot__who">
                <div className="mspot__avatar" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--heartwood)" strokeWidth="1.5">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M5 20a7 7 0 0 1 14 0" />
                  </svg>
                </div>
                <div>
                  <div className="mspot__role">Registered Patent Attorney</div>
                  <div className="mspot__redact" aria-hidden="true" />
                  <div className="mspot__redact-cap">Name shown in-app</div>
                </div>
              </div>

              <div className="mspot__cred-col">
                <div className="mspot__cred">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--brass)" strokeWidth="1.8">
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                  USPTO registered
                </div>
                <div className="mspot__reg">
                  <span className="mspot__reg-cap">Reg. no.</span>
                  <span className="mspot__reg-bar" aria-hidden="true" />
                </div>
              </div>
            </div>

            <div className="mspot__chips">
              <span className="mspot__chip">Mechanical · Consumer products</span>
              <span className="mspot__chip">Provisional + Utility</span>
              <span className="mspot__chip mspot__chip--acc">
                <span className="mspot__dot" aria-hidden="true" />
                Accepts micro-entity
              </span>
              <span className="mspot__firm">firm.com ↗</span>
            </div>
          </div>

          <div className="mspot__foot">
            <div className="mspot__stack" aria-hidden="true">
              <span className="mspot__sc" />
              <span className="mspot__sc" />
              <span className="mspot__sc" />
              <span className="mspot__sc" />
            </div>
            <span className="mspot__more">+8 more match these filters</span>
          </div>
        </div>

        <p className="mspot__caption">
          Sample profile. EntreDash aggregates the public USPTO roster and does
          not endorse individual practitioners. Verify any credential directly
          before you engage.
        </p>

        <div className="mspot__ctas">
          <Link href="/marketplace" className="mspot__cta-primary">
            Browse the marketplace
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
          <Link href="/marketplace" className="mspot__cta-ghost">
            How we vet listings
          </Link>
        </div>
      </div>
    </section>
  );
}

const MSPOT_CSS = `
.mspot {
  padding: clamp(72px, 12vh, 120px) 32px clamp(72px, 13vh, 130px);
  /* Ink near-black ground, matching the guarantees band, so the two dark
     sections read as one family (distinct from the Heartwood hero / spark-to-
     business bands). The spark-green glow reads even brighter against Ink. */
  background:
    radial-gradient(62% 52% at 50% 36%, rgba(79,217,146,0.16), transparent 72%),
    #0F1714;
  color: var(--parchment);
}
.mspot__inner {
  max-width: 680px;
  margin: 0 auto;
  text-align: center;
}
.mspot__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-family: var(--f-mono);
  /* Bigger + bolder (was 12px / 600) to match the enlarged section eyebrows. */
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--brass-light);
}
.mspot__tick { width: 28px; height: 2px; background: var(--brass); display: inline-block; }
.mspot__headline {
  font-size: clamp(34px, 4.6vw, 52px);
  font-weight: 400;
  line-height: 1.08;
  letter-spacing: -0.02em;
  color: var(--parchment);
  margin: 20px 0 0;
}
.mspot__head-em { font-style: italic; }
.mspot__sub {
  font-family: var(--f-sans);
  font-size: 16px;
  line-height: 1.5;
  color: rgba(244,239,226,0.72);
  max-width: 520px;
  margin: 18px auto 0;
}

/* Prominent roster stat — the section's headline credibility number, on the
   dark ground. Big spark-light figure (reads bright on heartwood), animated
   count-up. Centered, so the widening digit count during the count-up causes
   no horizontal layout shift for surrounding blocks. */
.mspot__roster { margin: 36px auto 0; text-align: center; }
.mspot__roster-num {
  font-size: clamp(60px, 9.5vw, 92px);
  line-height: 1;
  font-weight: 400;
  letter-spacing: -0.02em;
  color: var(--spark-light);
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: flex-start;
}
.mspot__roster-plus {
  font-size: 0.5em;
  line-height: 1;
  margin-top: 0.12em;
  margin-left: 0.04em;
  color: var(--brass-light);
}
.mspot__roster-label {
  font-family: var(--f-mono);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(244,239,226,0.62);
  margin-top: 12px;
}

.mspot__card {
  background: var(--surface-card);
  color: var(--ink);
  border-radius: 18px;
  max-width: 600px;
  margin: 44px auto 0;
  padding: 26px 30px;
  text-align: left;
  box-shadow: 0 2px 6px rgba(0,0,0,0.32), 0 34px 70px -24px rgba(0,0,0,0.6);
  opacity: 0;
  transform: translateY(16px);
  animation: mspotCardIn 600ms cubic-bezier(0.22,1,0.36,1) 0.05s forwards;
}
@keyframes mspotCardIn { to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .mspot__card { opacity: 1; transform: none; animation: none; }
}

.mspot__card-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.mspot__card-label {
  font-family: var(--f-mono);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(27,58,46,0.7);
}
.mspot__filter {
  font-family: var(--f-mono);
  font-size: 10px;
  letter-spacing: 0.05em;
  color: var(--heartwood);
  background: rgba(27,58,46,0.06);
  padding: 5px 10px;
  border-radius: 999px;
  white-space: nowrap;
}
.mspot__count {
  font-size: 62px;
  line-height: 1;
  font-weight: 400;
  color: var(--heartwood);
  margin-top: 18px;
  font-variant-numeric: tabular-nums;
  min-width: 1.4em;
}
.mspot__count-label { font-family: var(--f-sans); font-size: 14px; color: rgba(15,23,20,0.66); margin-top: 6px; }
.mspot__rule { height: 1px; background: rgba(27,58,46,0.1); margin: 20px 0; }

.mspot__profile { background: rgba(27,58,46,0.04); border-radius: 12px; padding: 16px; }
.mspot__profile-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
.mspot__who { display: flex; gap: 12px; }
.mspot__avatar {
  width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
  background: rgba(27,58,46,0.08);
  display: flex; align-items: center; justify-content: center;
}
.mspot__role { font-family: var(--f-sans); font-size: 14px; font-weight: 600; color: var(--ink); }
.mspot__redact { width: 130px; height: 11px; border-radius: 3px; background: rgba(27,58,46,0.16); margin-top: 7px; max-width: 100%; }
.mspot__redact-cap, .mspot__reg-cap {
  font-family: var(--f-mono);
  font-size: 9px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-decorative);
  margin-top: 5px;
}
.mspot__cred-col { text-align: right; flex-shrink: 0; }
.mspot__cred {
  display: flex; align-items: center; gap: 5px; justify-content: flex-end;
  font-family: var(--f-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--brass-deep);
}
.mspot__reg { display: flex; align-items: center; gap: 6px; justify-content: flex-end; margin-top: 6px; }
.mspot__reg-cap { margin-top: 0; }
.mspot__reg-bar { width: 42px; height: 8px; border-radius: 2px; background: rgba(181,133,60,0.35); }

.mspot__chips { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 14px; align-items: center; }
.mspot__chip {
  font-family: var(--f-sans);
  font-size: 12px;
  color: var(--heartwood);
  background: rgba(27,58,46,0.06);
  padding: 5px 11px;
  border-radius: 999px;
}
.mspot__chip--acc { background: rgba(31,157,99,0.1); display: inline-flex; align-items: center; gap: 6px; }
.mspot__dot { width: 6px; height: 6px; border-radius: 50%; background: var(--spark); }
.mspot__firm { margin-left: auto; font-family: var(--f-mono); font-size: 11px; color: var(--text-decorative); }

.mspot__foot { display: flex; align-items: center; gap: 10px; margin-top: 16px; }
.mspot__stack { display: flex; }
.mspot__sc { width: 26px; height: 26px; border-radius: 50%; background: rgba(27,58,46,0.1); border: 2px solid var(--surface-card); margin-left: -8px; }
.mspot__sc:first-child { margin-left: 0; }
.mspot__more { font-family: var(--f-sans); font-size: 13px; color: var(--text-decorative); }

.mspot__caption {
  font-family: var(--f-sans);
  font-size: 13px;
  line-height: 1.5;
  color: rgba(244,239,226,0.5);
  max-width: 480px;
  margin: 20px auto 0;
}

.mspot__ctas { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; margin-top: 28px; }
.mspot__cta-primary {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 15px 28px;
  background: var(--spark-light);
  color: var(--ink);
  font-family: var(--f-sans);
  font-size: 15px;
  font-weight: 600;
  border-radius: 999px;
  text-decoration: none;
  transition: transform 160ms ease, box-shadow 160ms ease;
}
.mspot__cta-primary:hover { transform: translateY(-1px); box-shadow: 0 8px 20px -8px rgba(79,217,146,0.55); }
.mspot__cta-ghost {
  display: inline-flex; align-items: center;
  padding: 15px 28px;
  background: transparent;
  border: 1px solid rgba(181,133,60,0.55);
  color: var(--parchment);
  font-family: var(--f-sans);
  font-size: 15px;
  font-weight: 500;
  border-radius: 999px;
  text-decoration: none;
  transition: border-color 160ms ease, background-color 160ms ease;
}
.mspot__cta-ghost:hover { border-color: var(--brass-light); background: rgba(181,133,60,0.08); }

@media (max-width: 640px) {
  .mspot__card-head { flex-direction: column; align-items: flex-start; gap: 8px; }
  .mspot__count { font-size: 52px; }
  .mspot__card { padding: 22px; }
  .mspot__cta-primary, .mspot__cta-ghost { width: 100%; justify-content: center; }
  /* Stack the profile row on phones so the "USPTO registered" credential block
     drops BELOW the name instead of being pushed off the right edge of the screen. */
  .mspot__profile-top { flex-direction: column; gap: 12px; }
  .mspot__cred-col { text-align: left; align-self: flex-start; }
  .mspot__cred { justify-content: flex-start; }
  .mspot__reg { justify-content: flex-start; }
}
`;
